/** Strict, read-only georeference validation for legacy uploaded imagery. */
export type LonLat = [number, number];

export interface ImageGeorefCorners {
  ul: LonLat;
  ur: LonLat;
  lr: LonLat;
  ll: LonLat;
}

export interface ImageGeoref {
  crs: 'EPSG:4326';
  corners: ImageGeorefCorners;
}

export interface WebMercatorCorners {
  topLeft: [number, number];
  topRight: [number, number];
  bottomRight: [number, number];
  bottomLeft: [number, number];
}

const EARTH_RADIUS = 6378137;
const MAX_WEB_MERCATOR_LAT = 85.05112878;

function validPosition(value: unknown): value is LonLat {
  return Array.isArray(value) && value.length === 2 && Number.isFinite(value[0]) && Number.isFinite(value[1])
    && Math.abs(value[0]) <= 180 && Math.abs(value[1]) <= MAX_WEB_MERCATOR_LAT;
}

/** Reject unprojectable, degenerate, or antimeridian-spanning stored corners. */
export function hasValidCorners(georef: unknown): georef is ImageGeoref {
  const candidate = georef as ImageGeoref | null;
  if (!candidate || candidate.crs !== 'EPSG:4326' || !candidate.corners) return false;
  const points = [candidate.corners.ul, candidate.corners.ur, candidate.corners.lr, candidate.corners.ll];
  if (!points.every(validPosition)) return false;
  const lons = points.map(([lon]) => lon);
  if (Math.max(...lons) - Math.min(...lons) > 180) return false;
  const area = points.reduce((sum, [x1, y1], index) => {
    const [x2, y2] = points[(index + 1) % points.length];
    return sum + x1 * y2 - x2 * y1;
  }, 0);
  return Math.abs(area) > 1e-12;
}

export function lonLatToWebMercator([lon, lat]: LonLat): [number, number] {
  return [
    EARTH_RADIUS * (lon * Math.PI / 180),
    EARTH_RADIUS * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI / 180) / 2)),
  ];
}

export function cornersToWebMercator(corners: ImageGeorefCorners): WebMercatorCorners {
  return {
    topLeft: lonLatToWebMercator(corners.ul),
    topRight: lonLatToWebMercator(corners.ur),
    bottomRight: lonLatToWebMercator(corners.lr),
    bottomLeft: lonLatToWebMercator(corners.ll),
  };
}
